import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchOrders, setFilters, updateOrderStatus } from '../store/slices/orderSlice';
import Modal from '../components/common/Modal';
import '../pages/ProductList.css';
import './Orders.css';

const fmtCurrency = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

export default function Orders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, filters, pagination, isLoading } = useSelector(s => s.orders);

  const [confirm, setConfirm] = useState(null); // { id, action }

  useEffect(() => { dispatch(fetchOrders(filters)); }, [dispatch, filters]);

  const onHeaderClick = (col) => {
    const dir = (filters.sortBy === col && filters.sortDir === 'asc') ? 'desc' : 'asc';
    dispatch(setFilters({ sortBy: col, sortDir: dir }));
  };

  const onChangePage = (delta) => {
    const next = Math.max(1, (filters.page || 1) + delta);
    dispatch(setFilters({ page: next }));
  };

  const requestAction = (id, action) => setConfirm({ id, action });
  const performAction = async () => {
    if (!confirm) return;
    const map = { Hold: 'On Hold', Cancel: 'Canceled', Ship: 'Shipped' };
    await dispatch(updateOrderStatus({ id: confirm.id, status: map[confirm.action] }))
      .unwrap()
      .catch(() => {});
    setConfirm(null);
  };

  return (
    <div className="orders page-wrapper">
      <div className="page-header orders-header">
        <div className="headings">
          <h1>Orders</h1>
          <p>Manage and track your customer orders</p>
        </div>
      </div>

      <div className="panel">
        <div className="filters-grid">
          <div className="field-group">
            <label>Order #</label>
            <input placeholder="123" type="number" onChange={(e)=>dispatch(setFilters({ orderNumber: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Customer PO #</label>
            <input placeholder="PO-2025-0001" onChange={(e)=>dispatch(setFilters({ customerPoNumber: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Company Name</label>
            <input placeholder="Acme Inc" onChange={(e)=>dispatch(setFilters({ companyName: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Customer Name</label>
            <input placeholder="Jane Doe" onChange={(e)=>dispatch(setFilters({ customerName: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Payment Method</label>
            <input placeholder="Credit Card" onChange={(e)=>dispatch(setFilters({ paymentMethod: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Ship Via</label>
            <input placeholder="UPS Ground" onChange={(e)=>dispatch(setFilters({ shipVia: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Status</label>
            <select onChange={(e)=>dispatch(setFilters({ status: e.target.value || undefined, page: 1 }))}>
              <option value="">All</option>
              {['Pending','On Hold','Canceled','Shipped'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>Order From</label>
            <input type="date" onChange={(e)=>dispatch(setFilters({ orderDateFrom: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Order To</label>
            <input type="date" onChange={(e)=>dispatch(setFilters({ orderDateTo: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Ship From</label>
            <input type="date" onChange={(e)=>dispatch(setFilters({ shipDateFrom: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Ship To</label>
            <input type="date" onChange={(e)=>dispatch(setFilters({ shipDateTo: e.target.value || undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Min Amount</label>
            <input placeholder="0.00" type="number" step="0.01" onChange={(e)=>dispatch(setFilters({ totalAmountMin: e.target.value !== '' ? Number(e.target.value) : undefined, page: 1 }))} />
          </div>
          <div className="field-group">
            <label>Max Amount</label>
            <input placeholder="9999.99" type="number" step="0.01" onChange={(e)=>dispatch(setFilters({ totalAmountMax: e.target.value !== '' ? Number(e.target.value) : undefined, page: 1 }))} />
          </div>
          <div className="field-group" style={{ alignSelf:'end' }}>
            <button className="btn text" onClick={()=>dispatch(setFilters({ orderNumber: undefined, customerPoNumber: undefined, companyName: undefined, customerName: undefined, paymentMethod: undefined, shipVia: undefined, status: undefined, orderDateFrom: undefined, orderDateTo: undefined, shipDateFrom: undefined, shipDateTo: undefined, totalAmountMin: undefined, totalAmountMax: undefined, page: 1 }))}>Clear Filters</button>
          </div>
        </div>
      </div>

      <div className="table-wrapper panel">
        <div className="table-scroll">
          <table className="data-table">
          <thead>
            <tr>
              {[
                ['orderNumber', 'Order #'], ['companyName', 'Company'], ['orderDate', 'Order Date'], ['shipDate', 'Ship Date'],
                ['totalAmount', 'Total'], ['customerName', 'Customer'], ['statusSimple', 'Status'], ['actions', 'Actions']
              ].map(([key, label]) => (
                <th key={key} onClick={()=> key!=='actions' && onHeaderClick(key)} style={{ cursor: key!=='actions' ? 'pointer' : 'default' }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} style={{ textAlign: 'center' }}>Loading...</td></tr>
            )}
            {!isLoading && orders?.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center' }}>No orders</td></tr>
            )}
            {orders?.map(o => (
              <tr key={o._id} onClick={(e)=>{
                // don't trigger row nav if interacting with select
                const tag = (e.target.tagName || '').toLowerCase();
                if (tag === 'select' || tag === 'option' || tag === 'button') return;
                navigate(`/orders/${o._id}`);
              }}>
                <td>{o.orderNumber || '-'}</td>
                <td>{o.companyName || '-'}</td>
                <td>{fmtDate(o.orderDate)}</td>
                <td>{fmtDate(o.shipDate)}</td>
                <td>{fmtCurrency(o.totalAmount)}</td>
                <td>{o.customerName || '-'}</td>
                <td>
                  <span className={`status-pill ${
                    (o.statusSimple||'Pending').toLowerCase().replace(' ', '-')
                  }`}>{o.statusSimple || 'Pending'}</span>
                </td>
                <td className="actions-cell">
                  <select className="action-select" defaultValue="" onChange={(e)=>{
                    const v = e.target.value; e.target.value = '';
                    if (v === 'Edit') navigate(`/orders/${o._id}/edit`);
                    if (v === 'Hold') requestAction(o._id, 'Hold');
                    if (v === 'Cancel') requestAction(o._id, 'Cancel');
                    if (v === 'Ship') requestAction(o._id, 'Ship');
                  }} onClick={(e)=>e.stopPropagation()}>
                    <option value="" disabled>Actions…</option>
                    <option value="Edit">Edit</option>
                    <option value="Hold">Hold</option>
                    <option value="Cancel">Cancel</option>
                    <option value="Ship">Mark Shipped</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="pagination-bar">
          <div className="pager-info">Page {pagination.currentPage || 1} of {pagination.totalPages || 1}</div>
          <div className="pager-buttons">
            <button type="button" className="btn text" onClick={()=>onChangePage(-1)} disabled={(filters.page||1) <= 1}>Prev</button>
            <button type="button" className="btn text" onClick={()=>onChangePage(1)} disabled={(pagination.currentPage||1) >= (pagination.totalPages||1)}>Next</button>
          </div>
        </div>
      </div>

      <Modal isOpen={!!confirm} onClose={()=>setConfirm(null)}>
        <div style={{ padding: 12 }}>
          <h3>Confirm {confirm?.action}</h3>
          <p>Are you sure you want to {confirm?.action?.toLowerCase()} this order?</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={performAction}>Confirm</button>
            <button onClick={()=>setConfirm(null)}>Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
